import { EFiscalTaxRegime } from "./EFiscalTaxRegime"
import { type IFiscalProductProfile } from "./IFiscalProductProfile"

export type FiscalProductProfileField =
  | "productCode"
  | "ncm"
  | "cest"
  | "cfop"
  | "unit"
  | "origin"
  | "csosn"
  | "icmsCst"
  | "icmsRate"
  | "pisCst"
  | "pisRate"
  | "cofinsCst"
  | "cofinsRate"
  | "taxBenefitCode"
  | "ibsCbsCst"
  | "ibsCbsClassificationCode"
  | "ibsStateRate"
  | "ibsMunicipalRate"
  | "cbsRate"

export type FiscalProductProfileValidationMessageKey =
  | "validation.productCode"
  | "validation.ncm"
  | "validation.cest"
  | "validation.cfop"
  | "validation.unit"
  | "validation.origin"
  | "validation.csosn"
  | "validation.csosnMei"
  | "validation.icmsCst"
  | "validation.pisCst"
  | "validation.cofinsCst"
  | "validation.rate"
  | "validation.taxBenefitCode"
  | "validation.ibsCbsCst"
  | "validation.ibsCbsClassification"

export type FiscalProductProfileValidationErrors = Partial<
  Record<FiscalProductProfileField, FiscalProductProfileValidationMessageKey>
>

const digitsOnly = (value?: string) => (value ?? "").replace(/\D/g, "")

const hasValue = (value?: string) => Boolean(value?.trim())

const isValidRate = (value: number) => Number.isFinite(value) && value >= 0 && value <= 100

export const validateFiscalProductProfile = (
  profile: IFiscalProductProfile,
  taxRegime: EFiscalTaxRegime,
): FiscalProductProfileValidationErrors => {
  const errors: FiscalProductProfileValidationErrors = {}

  const productCode = profile.productCode.trim()

  const ncm = digitsOnly(profile.ncm)

  const cest = digitsOnly(profile.cest)

  const cfop = digitsOnly(profile.cfop)

  const unit = profile.unit.trim()

  const csosn = digitsOnly(profile.csosn)

  const icmsCst = digitsOnly(profile.icmsCst)

  const pisCst = digitsOnly(profile.pisCst)

  const cofinsCst = digitsOnly(profile.cofinsCst)

  const taxBenefitCode = profile.taxBenefitCode?.trim() ?? ""

  const ibsCbsCst = digitsOnly(profile.ibsCbsCst)

  const ibsCbsClassificationCode = digitsOnly(profile.ibsCbsClassificationCode)

  if (productCode.length > 60) {
    errors.productCode = "validation.productCode"
  }

  if (ncm.length !== 8) {
    errors.ncm = "validation.ncm"
  }

  if (hasValue(profile.cest) && cest.length !== 7) {
    errors.cest = "validation.cest"
  }

  if (cfop.length !== 4) {
    errors.cfop = "validation.cfop"
  }

  if (unit.length === 0 || unit.length > 6) {
    errors.unit = "validation.unit"
  }

  if (!Number.isInteger(profile.origin) || profile.origin < 0 || profile.origin > 8) {
    errors.origin = "validation.origin"
  }

  if (taxRegime === EFiscalTaxRegime.MicroEntrepreneur) {
    if (csosn !== "102" && csosn !== "300") {
      errors.csosn = "validation.csosnMei"
    }
  } else if (taxRegime === EFiscalTaxRegime.SimpleNationalExcess || taxRegime === EFiscalTaxRegime.Normal) {
    if (icmsCst.length !== 2 && icmsCst.length !== 3) {
      errors.icmsCst = "validation.icmsCst"
    }
  } else if (csosn.length !== 3) {
    errors.csosn = "validation.csosn"
  }

  if (pisCst.length !== 2) {
    errors.pisCst = "validation.pisCst"
  }

  if (cofinsCst.length !== 2) {
    errors.cofinsCst = "validation.cofinsCst"
  }

  if (taxBenefitCode.length > 10) {
    errors.taxBenefitCode = "validation.taxBenefitCode"
  }

  const hasAnyTaxReformField = hasValue(profile.ibsCbsCst) || hasValue(profile.ibsCbsClassificationCode)
  const requiresTaxReformFields = taxRegime === EFiscalTaxRegime.Normal || hasAnyTaxReformField

  if (requiresTaxReformFields && ibsCbsCst.length !== 3) {
    errors.ibsCbsCst = "validation.ibsCbsCst"
  }

  if (requiresTaxReformFields && ibsCbsClassificationCode.length !== 6) {
    errors.ibsCbsClassificationCode = "validation.ibsCbsClassification"
  }

  const rateFields: Array<
    [
      keyof Pick<
        IFiscalProductProfile,
        "icmsRate" | "pisRate" | "cofinsRate" | "ibsStateRate" | "ibsMunicipalRate" | "cbsRate"
      >,
      number,
    ]
  > = [
    ["icmsRate", profile.icmsRate],
    ["pisRate", profile.pisRate],
    ["cofinsRate", profile.cofinsRate],
    ["ibsStateRate", profile.ibsStateRate],
    ["ibsMunicipalRate", profile.ibsMunicipalRate],
    ["cbsRate", profile.cbsRate],
  ]

  rateFields.forEach(([field, value]) => {
    if (!isValidRate(value)) {
      errors[field] = "validation.rate"
    }
  })

  return errors
}
